import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const apiFetch = (path, method = 'GET', body) =>
  fetch(`/${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
    body: body ? JSON.stringify(body) : undefined,
  }).then(r => r.json());

const FUNCIONALIDADES = [
  { path: '/dashboard-revista', icone: '📊', cor: '#3b82f6', corBg: '#eff6ff', label: 'Dashboard da Revista', descricao: 'O mapa da semana: onde o Brasil está registrando marcas, estado por estado, com filtros de despacho e procurador.', disponivel: true },
  { path: '/pesquisa-marca', icone: '🔎', cor: '#16a34a', corBg: '#f0fdf4', label: 'Pesquisa de Marca', descricao: 'Digite o nome e veja na hora se a marca tem caminho livre — semáforo, análise fonética e parecer em PDF pro cliente.', disponivel: true },
  { path: '/consultar-rpi', icone: '📚', cor: '#7c3aed', corBg: '#f5f3ff', label: 'Consultar RPI', descricao: 'Fuce qualquer revista importada: procurador, titular, marca, classe, despacho, UF, situação e datas.', disponivel: true },
  { path: '/oposicao', icone: '🛡️', cor: '#dc2626', corBg: '#fef2f2', label: 'Análise de Oposição', descricao: 'Cruza a revista nova com as marcas dos seus clientes e aponta quem está chegando perto demais — com o fundamento legal pronto.', disponivel: true },
  { path: '/demandas', icone: '⏰', cor: '#0a1628', corBg: '#e8edf2', label: 'Alertas de Prazo', descricao: 'A revista cria os prazos sozinha — recurso, exigência, manifestação, prorrogação — e o WhatsApp avisa 7 dias antes, 1 dia antes e no dia.', disponivel: true, selo: 'NO AR' },
];

function StatusTag({ situacao }) {
  if (!situacao) return <span style={{ color: '#9ca3af', fontSize: 12 }}>—</span>;
  const s = situacao.toLowerCase();
  let bg = '#f3f4f6', color = '#374151';
  if (s.includes('inativ') || s.includes('indefer') || s.includes('arquiv') || s.includes('extin') || s.includes('caduc')) { bg = '#fee2e2'; color = '#dc2626'; }
  else if (s.includes('ativa') || s.includes('vigor') || s.includes('concedid') || s.includes('vigente')) { bg = '#dcfce7'; color = '#15803d'; }
  else if (s.includes('andamento') || s.includes('protocolado') || s.includes('exame') || s.includes('aguardando') || s.includes('pendente')) { bg = '#fef9c3'; color = '#a16207'; }
  return <span style={{ background: bg, color, fontSize: 11, fontWeight: 600, padding: '1px 7px', borderRadius: 10 }}>{situacao}</span>;
}

// Famílias de situação da vigilância. A base local guarda a situação resumida
// ("Ativa" / "Em andamento" / "Inativa"); o detalhe fino (oposição, exigência,
// recurso) vive nos Alertas de Prazo. "Inativ" vem antes de "ativ" de propósito.
const GRUPOS = [
  { id: 'inativa',   rotulo: '🔴 Inativas', teste: s => s.includes('inativ') || s.includes('indefer') || s.includes('arquiv') || s.includes('extin') || s.includes('caduc') },
  { id: 'andamento', rotulo: '⏳ Em andamento', teste: s => s.includes('andamento') || s.includes('protocolado') || s.includes('exame') || s.includes('aguard') || s.includes('pendente') || s.includes('sobrest') },
  { id: 'ativa',     rotulo: '✅ Ativas', teste: s => s.includes('ativa') || s.includes('vigor') || s.includes('concedid') || s.includes('vigente') },
  { id: 'outros',    rotulo: '📌 Outras', teste: () => true },
];
const SEM_SITUACAO = { id: 'sem', rotulo: '❔ Sem situação' };
const grupoDe = sit => {
  const s = (sit || '').toLowerCase();
  return s ? GRUPOS.find(g => g.teste(s)) : SEM_SITUACAO;
};
const ORDEM = { andamento: 0, inativa: 1, sem: 2, outros: 3, ativa: 4 };

export default function Inpi() {
  const navigate = useNavigate();
  const [carteira, setCarteira] = useState(null);
  const [busca, setBusca] = useState('');
  const [grupoSel, setGrupoSel] = useState('');
  const [sincronizando, setSincronizando] = useState(false);
  const [msgSync, setMsgSync] = useState('');
  const [copiado, setCopiado] = useState(null);
  const admin = (localStorage.getItem('cargoUsuario') || '').toLowerCase().includes('administrador');

  // 📋 ao lado do nº do processo (pedido 04/11/2026) — copia sem abrir a
  // ficha do cliente (o clique na linha continua abrindo)
  function copiar(e, numero) {
    e.stopPropagation();
    const fallback = () => {
      const el = document.createElement('textarea');
      el.value = numero; document.body.appendChild(el); el.select();
      document.execCommand('copy'); document.body.removeChild(el);
    };
    if (navigator.clipboard) navigator.clipboard.writeText(numero).catch(fallback); else fallback();
    setCopiado(numero);
    setTimeout(() => setCopiado(c => (c === numero ? null : c)), 1500);
  }

  const carregar = () => apiFetch('inpi/status-geral')
    .then(d => setCarteira(Array.isArray(d?.processos) ? d.processos : []))
    .catch(() => setCarteira([]));
  useEffect(() => { carregar(); }, []);

  async function sincronizar() {
    setSincronizando(true);
    setMsgSync('');
    try {
      const r = await apiFetch('inpi/verificar-agora', 'POST');
      if (r.erro) setMsgSync(`⚠ ${r.erro}`);
      else setMsgSync(`✅ ${r.processos_verificados} processo(s) conferidos na base · ${(r.mudancas_situacao || []).length} mudança(s) de situação${r.nao_encontrados ? ` · ${r.nao_encontrados} sem registro na base` : ''}`);
      carregar();
    } catch {
      setMsgSync('⚠ Erro ao sincronizar.');
    }
    setSincronizando(false);
  }

  const lista = carteira || [];
  const contagens = {};
  lista.forEach(p => { const g = grupoDe(p.situacao); contagens[g.id] = (contagens[g.id] || 0) + 1; });
  const chips = [...GRUPOS, SEM_SITUACAO]
    .filter(g => contagens[g.id])
    .sort((a, b) => ORDEM[a.id] - ORDEM[b.id]);
  const termo = busca.trim().toLowerCase();
  const visiveis = lista
    .filter(p => !grupoSel || grupoDe(p.situacao).id === grupoSel)
    .filter(p => !termo || (p.marca || '').toLowerCase().includes(termo) || (p.cliente || '').toLowerCase().includes(termo) || p.numero.includes(termo))
    .sort((a, b) => (ORDEM[grupoDe(a.situacao).id] - ORDEM[grupoDe(b.situacao).id]) || (a.marca || '').localeCompare(b.marca || ''));

  function handleClick(item) {
    if (item.atalho) { navigate(item.atalho); return; }
    if (item.path) { navigate(item.path); }
  }

  return (
    <div style={{ maxWidth: 900 }}>
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ width: 44, height: 44, borderRadius: 12, background: 'linear-gradient(135deg, #0a1628, #1e3a5f)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, boxShadow: '0 2px 6px rgba(10,22,40,.25)' }}>🏛️</div>
        <div>
          <h1 style={{ fontSize: 18, fontWeight: 800, color: '#0a1628', margin: 0 }}>Central INPI</h1>
          <p style={{ color: '#6b7280', fontSize: 12.5, margin: '1px 0 0' }}>Tudo do Instituto Nacional da Propriedade Industrial num lugar só — da pesquisa ao prazo.</p>
        </div>
      </div>

      {/* Funcionalidades */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(255px, 1fr))', gap: 12 }}>
        {FUNCIONALIDADES.map((item, i) => {
          const clicavel = item.disponivel && (item.path || item.atalho);
          return (
            <div key={i}
              onClick={() => clicavel && handleClick(item)}
              style={{
                background: '#fff', borderRadius: 10, border: '1px solid #dbe2ea', borderTop: `3px solid ${item.cor}`,
                padding: '14px 14px 12px', cursor: clicavel ? 'pointer' : 'default',
                position: 'relative', boxShadow: '0 1px 3px rgba(10,22,40,.06)',
                transition: 'transform .12s ease, box-shadow .12s ease',
              }}
              onMouseEnter={e => { if (clicavel) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(10,22,40,.13)'; }}}
              onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(10,22,40,.06)'; }}
            >
              {item.selo && (
                <span style={{ position: 'absolute', top: 10, right: 10, background: '#dcfce7', color: '#15803d', fontSize: 10, fontWeight: 800, padding: '2px 8px', borderRadius: 10, letterSpacing: '.03em' }}>{item.selo}</span>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, background: item.corBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 19, flexShrink: 0 }}>{item.icone}</div>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: '#0a1628', lineHeight: 1.25 }}>{item.label}</div>
              </div>
              <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.55 }}>{item.descricao}</div>
              {item.info && (
                <div style={{ marginTop: 8, fontSize: 11, color: '#0369a1', background: '#f0f9ff', borderRadius: 5, padding: '3px 8px', display: 'inline-block' }}>
                  {item.info}
                </div>
              )}
              {clicavel && <div style={{ marginTop: 10, fontSize: 12, fontWeight: 700, color: item.cor }}>Acessar →</div>}
            </div>
          );
        })}
      </div>
      {/* Vigilância da carteira — abaixo dos cartões (pedido do Vagner, 08/10/2026) */}
      <div style={{ background: '#fff', border: '1px solid #dbe2ea', borderRadius: 10, marginTop: 16, overflow: 'hidden', boxShadow: '0 1px 3px rgba(10,22,40,.06)' }}>
        <div style={{ background: 'linear-gradient(90deg, #f0f4f8, #fafcfe)', padding: '8px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 17 }}>📡</span>
            <div>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: '#0a1628' }}>Vigilância da carteira</div>
              <div style={{ fontSize: 11, color: '#6b7280' }}>
                Situação de todos os processos dos clientes — atualiza sozinha a cada revista e avisa no grupo do WhatsApp quando algo muda{lista.length > 0 ? ` · ${lista.length} processo(s)` : ''}.
              </div>
            </div>
          </div>
          {admin && (
            <button onClick={sincronizar} disabled={sincronizando}
              style={{ padding: '5px 14px', background: sincronizando ? '#e5e7eb' : '#0a1628', color: sincronizando ? '#9ca3af' : '#fff', border: 'none', borderRadius: 6, fontWeight: 600, fontSize: 12, cursor: sincronizando ? 'default' : 'pointer' }}>
              {sincronizando ? 'Sincronizando…' : '🔄 Sincronizar com a base'}
            </button>
          )}
        </div>

        {msgSync && (
          <div style={{ margin: '8px 12px', padding: '6px 10px', background: msgSync.startsWith('⚠') ? '#fef2f2' : '#f0fdf4', border: `1px solid ${msgSync.startsWith('⚠') ? '#fecaca' : '#bbf7d0'}`, borderRadius: 6, fontSize: 12, color: msgSync.startsWith('⚠') ? '#b91c1c' : '#15803d' }}>
            {msgSync}
          </div>
        )}

        {carteira === null ? (
          <div style={{ color: '#9ca3af', fontSize: 12, padding: '10px 14px' }}>Carregando…</div>
        ) : lista.length === 0 ? (
          <div style={{ color: '#6b7280', fontSize: 12, padding: '10px 14px' }}>
            Nenhum processo cadastrado nos clientes ainda.
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', padding: '8px 12px 4px' }}>
              {chips.map(g => {
                const ativo = grupoSel === g.id;
                return (
                  <button key={g.id} onClick={() => setGrupoSel(ativo ? '' : g.id)}
                    style={{ padding: '3px 10px', borderRadius: 12, fontSize: 11.5, fontWeight: 600, cursor: 'pointer', border: `1px solid ${ativo ? '#0a1628' : '#d1d5db'}`, background: ativo ? '#0a1628' : '#fff', color: ativo ? '#fff' : '#374151' }}>
                    {g.rotulo} · {contagens[g.id]}
                  </button>
                );
              })}
              <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="🔎 marca, cliente ou nº do processo"
                style={{ marginLeft: 'auto', padding: '4px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, minWidth: 210 }} />
            </div>
            <div style={{ maxHeight: 340, overflowY: 'auto', overflowX: 'auto', margin: '6px 0 0' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr>
                    {['Marca', 'Processo', 'Cliente', 'Situação'].map(h => (
                      <th key={h} style={{ padding: '5px 10px', textAlign: 'left', fontWeight: 700, color: '#fff', fontSize: 11, background: '#0a1628', textTransform: 'uppercase', letterSpacing: '0.04em', position: 'sticky', top: 0 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visiveis.length === 0 ? (
                    <tr><td colSpan={4} style={{ padding: '10px 14px', color: '#9ca3af', fontSize: 12 }}>Nada encontrado com esse filtro.</td></tr>
                  ) : visiveis.map((p, i) => (
                    <tr key={p.numero + i} onClick={() => p.cliente_id && navigate(`/clientes/${p.cliente_id}/editar`)}
                      title="Abrir a ficha do cliente"
                      style={{ background: i % 2 === 0 ? '#fff' : '#fafafa', borderBottom: '1px solid #e8eaed', cursor: p.cliente_id ? 'pointer' : 'default' }}>
                      <td style={{ padding: '5px 10px', fontWeight: 600, color: '#0a1628' }}>{p.marca || '—'}</td>
                      <td style={{ padding: '5px 10px', whiteSpace: 'nowrap' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#374151' }}>{p.numero}</span>
                          <button type="button" onClick={e => copiar(e, p.numero)} title="Copiar nº do processo"
                            style={{ background: copiado === p.numero ? '#dcfce7' : '#f3f4f6', color: copiado === p.numero ? '#15803d' : '#374151', border: '1px solid #d1d5db', borderRadius: 3, padding: '1px 6px', fontSize: 10.5, cursor: 'pointer', lineHeight: '16px' }}>
                            {copiado === p.numero ? '✓' : '📋'}
                          </button>
                        </span>
                      </td>
                      <td style={{ padding: '5px 10px', color: '#6b7280' }}>{p.cliente || '—'}</td>
                      <td style={{ padding: '5px 10px' }}><StatusTag situacao={p.situacao} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

    </div>
  );
}
